export const INITIAL_MODBUS_DEVICE_FORM = Object.freeze({
  name: "",
  transport: "tcp",
  host: "",
  port: "502",
  rtuPort: "",
  baudRate: "19200",
  dataBits: "8",
  stopBits: "1",
  parity: "even",
  unitId: "1",
  timeoutMs: "2000",
  reconnectMinMs: "250",
  reconnectMaxMs: "30000",
  enabled: true,
});

const parseInteger = (value) => {
  if (typeof value === "number") return value;
  const text = String(value ?? "").trim();
  return text === "" ? Number.NaN : Number(text);
};

const requireIntegerInRange = (value, label, min, max) => {
  const number = parseInteger(value);
  if (!Number.isInteger(number) || number < min || number > max) {
    return `${label} must be a whole number from ${min} to ${max}.`;
  }
  return "";
};

const IPV6_ZONE_RE = /^[A-Za-z0-9._-]{1,64}$/;

const isIPv4Address = (value) => {
  const parts = value.split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part) || (part.length > 1 && part.startsWith("0"))) return false;
    const octet = Number(part);
    return octet >= 0 && octet <= 255;
  });
};

const looksLikeDottedIPv4 = (value) => {
  const parts = value.split(".");
  return parts.length === 4 && parts.every((part) => /^\d+$/.test(part));
};

const isIPv6Address = (value) => {
  let address = value;
  const percent = address.indexOf("%");
  if (percent >= 0) {
    if (percent !== address.lastIndexOf("%") || !IPV6_ZONE_RE.test(address.slice(percent + 1))) return false;
    address = address.slice(0, percent);
  }
  if (!address.includes(":")) return false;

  const compression = address.indexOf("::");
  if (compression >= 0 && compression !== address.lastIndexOf("::")) return false;
  const compressed = compression >= 0;
  const left = compressed ? address.slice(0, compression) : address;
  const right = compressed ? address.slice(compression + 2) : "";
  const leftParts = left ? left.split(":") : [];
  const rightParts = right ? right.split(":") : [];
  const parts = [...leftParts, ...rightParts];
  if (parts.some((part) => part === "")) return false;

  let units = 0;
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (part.includes(".")) {
      // An embedded IPv4 address occupies the final two 16-bit words and may
      // appear only at the end of the complete IPv6 address.
      if (index !== parts.length - 1 || (compressed && rightParts.length === 0) || !isIPv4Address(part)) return false;
      units += 2;
      continue;
    }
    if (!/^[0-9A-Fa-f]{1,4}$/.test(part)) return false;
    units += 1;
  }
  return compressed ? units < 8 : units === 8;
};

const isDNSHost = (value) => {
  const absolute = value.endsWith(".") ? value.slice(0, -1) : value;
  if (!absolute || absolute.length > 253) return false;
  return absolute.split(".").every((label) => (
    label.length >= 1 &&
    label.length <= 63 &&
    /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(label)
  ));
};

export function validateModbusHost(value) {
  const host = String(value || "").trim();
  if (!host) return "Host is required.";
  if (host.includes("://") || host.startsWith("//")) {
    return "Host must not include a URL scheme. Enter only a DNS name or IP address.";
  }
  if (host.includes("[") || host.includes("]")) {
    return "Host must be unbracketed. Enter IPv6 like fe80::1 or fe80::1%eth0.";
  }

  const percent = host.indexOf("%");
  if (percent >= 0) {
    if (percent !== host.lastIndexOf("%") || percent === 0 || percent === host.length - 1) {
      return "Host has an invalid IPv6 zone.";
    }
    if (!IPV6_ZONE_RE.test(host.slice(percent + 1))) {
      return "IPv6 zone may contain only letters, numbers, '.', '_', or '-'.";
    }
    if (!isIPv6Address(host)) {
      return "IPv6 zones are valid only on an unbracketed IPv6 address.";
    }
    return "";
  }

  if (isIPv4Address(host) || isIPv6Address(host)) return "";
  if ((host.match(/:/g) || []).length === 1) {
    return "Host must not include a port. Use the TCP port field.";
  }
  if (host.includes(":")) return "Host must be a valid unbracketed IPv6 address.";
  if (looksLikeDottedIPv4(host) || !isDNSHost(host)) {
    return "Host must be a valid DNS name, IPv4 address, or unbracketed IPv6 address.";
  }
  return "";
}

export function modbusDeviceFormFromRecord(device) {
  if (!device) return { ...INITIAL_MODBUS_DEVICE_FORM };
  return {
    name: device.name || "",
    transport: device.transport === "rtu" ? "rtu" : "tcp",
    host: device.tcp?.host || "",
    port: String(device.tcp?.port ?? 502),
    rtuPort: device.rtu?.port || "",
    baudRate: String(device.rtu?.baud_rate ?? 19200),
    dataBits: String(device.rtu?.data_bits ?? 8),
    stopBits: String(device.rtu?.stop_bits ?? 1),
    parity: device.rtu?.parity || "even",
    unitId: String(device.unit_id ?? 1),
    timeoutMs: String(device.timeout_ms ?? 2000),
    reconnectMinMs: String(device.reconnect_min_ms ?? 250),
    reconnectMaxMs: String(device.reconnect_max_ms ?? 30000),
    enabled: device.enabled !== false,
  };
}

export function validateModbusDeviceForm(form) {
  if (!String(form?.name || "").trim()) return "Name is required.";

  const transport = form?.transport === "rtu" ? "rtu" : "tcp";
  if (transport === "tcp") {
    const hostError = validateModbusHost(form?.host);
    if (hostError) return hostError;
  } else {
    const serialPort = String(form?.rtuPort || "").trim();
    if (!serialPort) return "Serial port is required.";
    const containsControlCharacter = Array.from(serialPort).some((character) => {
      const code = character.charCodeAt(0);
      return code < 0x20 || code === 0x7f;
    });
    if (serialPort.length > 4096 || containsControlCharacter)
      return "Serial port contains invalid characters.";
    const serialChecks = [
      requireIntegerInRange(form.baudRate, "Baud rate", 300, 4000000),
      requireIntegerInRange(form.dataBits, "Data bits", 8, 8),
      requireIntegerInRange(form.stopBits, "Stop bits", 1, 2),
    ];
    const invalidSerial = serialChecks.find(Boolean);
    if (invalidSerial) return invalidSerial;
    if (!["none", "even", "odd"].includes(form.parity))
      return "Parity must be none, even, or odd.";
  }

  const checks = [
    ...(transport === "tcp"
      ? [requireIntegerInRange(form.port, "TCP port", 1, 65535)]
      : []),
    requireIntegerInRange(
      form.unitId,
      "Unit ID",
      transport === "rtu" ? 1 : 0,
      transport === "rtu" ? 247 : 255
    ),
    requireIntegerInRange(form.timeoutMs, "Request timeout", 50, 60000),
    requireIntegerInRange(
      form.reconnectMinMs,
      "Minimum reconnect delay",
      50,
      60000
    ),
    requireIntegerInRange(
      form.reconnectMaxMs,
      "Maximum reconnect delay",
      50,
      300000
    ),
  ];
  const invalid = checks.find(Boolean);
  if (invalid) return invalid;

  if (parseInteger(form.reconnectMaxMs) < parseInteger(form.reconnectMinMs)) {
    return "Maximum reconnect delay must be at least the minimum reconnect delay.";
  }
  return "";
}

const normalizedRTUBusProfile = (value) => ({
  port: String(value?.rtuPort ?? value?.rtu?.port ?? value?.port ?? "").trim(),
  baudRate: parseInteger(value?.baudRate ?? value?.rtu?.baud_rate ?? 19200),
  dataBits: parseInteger(value?.dataBits ?? value?.rtu?.data_bits ?? 8),
  stopBits: parseInteger(value?.stopBits ?? value?.rtu?.stop_bits ?? 1),
  parity: String(value?.parity ?? value?.rtu?.parity ?? "even").toLowerCase(),
  timeoutMs: parseInteger(value?.timeoutMs ?? value?.timeout_ms ?? 2000),
  reconnectMinMs: parseInteger(
    value?.reconnectMinMs ?? value?.reconnect_min_ms ?? 250
  ),
  reconnectMaxMs: parseInteger(
    value?.reconnectMaxMs ?? value?.reconnect_max_ms ?? 30000
  ),
});

const sameRTUBusProfile = (left, right) => {
  const a = normalizedRTUBusProfile(left);
  const b = normalizedRTUBusProfile(right);
  return Object.keys(a).every((key) => a[key] === b[key]);
};

export function describeModbusRTUBus(value) {
  const profile = normalizedRTUBusProfile(value);
  const parity = profile.parity.slice(0, 1).toUpperCase();
  return `${profile.baudRate} ${parity}8${profile.stopBits} · ${profile.timeoutMs} ms timeout · reconnect ${profile.reconnectMinMs}–${profile.reconnectMaxMs} ms`;
}

// Mirrors the backend's authoritative pre-persistence validation so operators
// see a useful conflict before submitting. Disabled definitions may reserve a
// future profile; only enabled slaves own the live bus and unit address.
export function validateModbusRTUBusAssignment(
  form,
  devices = [],
  editingDeviceId = ""
) {
  if (form?.transport !== "rtu" || form?.enabled === false) return "";
  const port = String(form.rtuPort || "").trim();
  const unitId = parseInteger(form.unitId);
  for (const device of devices) {
    if (
      device?.id === editingDeviceId ||
      device?.enabled === false ||
      device?.transport !== "rtu" ||
      String(device?.rtu?.port || "").trim() !== port
    ) {
      continue;
    }
    const peerName = String(device.name || "another RTU slave");
    if (!sameRTUBusProfile(form, device)) {
      return `Serial bus ${port} already uses a different line or timing profile on “${peerName}”.`;
    }
    if (Number(device.unit_id) === unitId) {
      return `Unit ID ${unitId} is already used by “${peerName}” on serial bus ${port}.`;
    }
  }
  return "";
}

export function groupModbusRTUBuses(devices = []) {
  const groups = new Map();
  devices
    .filter((device) => device?.transport === "rtu")
    .forEach((device) => {
      const profile = normalizedRTUBusProfile(device);
      const key = profile.port;
      if (!groups.has(key)) {
        groups.set(key, {
          port: profile.port,
          profile: describeModbusRTUBus(device),
          rawProfile: profile,
          hasEnabledProfile: device.enabled !== false,
          compatible: true,
          slaves: [],
        });
      } else if (device.enabled !== false) {
        const group = groups.get(key);
        if (group.hasEnabledProfile && !sameRTUBusProfile(group.rawProfile, profile)) {
          group.compatible = false;
        } else if (!group.hasEnabledProfile) {
          group.rawProfile = profile;
          group.profile = describeModbusRTUBus(device);
          group.hasEnabledProfile = true;
        }
      }
      groups.get(key).slaves.push({
        name: String(device.name || "Unnamed RTU slave"),
        unitId: Number(device.unit_id),
        enabled: device.enabled !== false,
      });
    });
  return [...groups.values()]
    .map((group) => {
      const result = { ...group };
      delete result.rawProfile;
      delete result.hasEnabledProfile;
      result.slaves = result.slaves.sort(
        (left, right) => left.unitId - right.unitId || left.name.localeCompare(right.name)
      );
      return result;
    })
    .sort((left, right) => left.port.localeCompare(right.port));
}

export function buildModbusDevicePayload(form) {
  const transport = form.transport === "rtu" ? "rtu" : "tcp";
  const payload = {
    name: String(form.name).trim(),
    transport,
    unit_id: parseInteger(form.unitId),
    timeout_ms: parseInteger(form.timeoutMs),
    reconnect_min_ms: parseInteger(form.reconnectMinMs),
    reconnect_max_ms: parseInteger(form.reconnectMaxMs),
    enabled: !!form.enabled,
  };
  if (transport === "rtu") {
    payload.rtu = {
      port: String(form.rtuPort).trim(),
      baud_rate: parseInteger(form.baudRate),
      data_bits: parseInteger(form.dataBits),
      stop_bits: parseInteger(form.stopBits),
      parity: form.parity,
    };
  } else {
    payload.tcp = {
      host: String(form.host).trim(),
      port: parseInteger(form.port),
    };
  }
  return payload;
}

export function deriveModbusDeviceStatus(device, state, stateError) {
  if (device?.enabled === false) return { key: "disabled", label: "Disabled" };

  const backendStatus = String(state?.status || "").trim().toLowerCase();
  const labels = {
    online: "Online",
    degraded: "Degraded",
    offline: "Offline",
    disabled: "Disabled",
  };
  if (labels[backendStatus]) {
    return {
      key: backendStatus,
      label: stateError ? `${labels[backendStatus]} (last known)` : labels[backendStatus],
    };
  }
  if (stateError) return { key: "unavailable", label: "Unavailable" };
  return { key: "checking", label: "Checking" };
}
